import { useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTrustedDevice } from '@/hooks/useTrustedDevice';

interface MFAGuardProps {
  children: ReactNode;
}

/** Vérifie si le groupe de l'utilisateur doit passer le MFA et redirige si nécessaire */
export function MFAGuard({ children }: MFAGuardProps) {
  const { user, session, loading: authLoading } = useAuth();
  const { checkTrusted } = useTrustedDevice();
  const navigate = useNavigate();
  const location = useLocation();
  // Track quel access_token a déjà été vérifié (se reset à chaque nouvelle session)
  const checkedToken = useRef<string | null>(null);

  // Ne pas re-checker si on est déjà sur /mfa ou /auth
  const isExempt =
    location.pathname.startsWith('/mfa') ||
    location.pathname.startsWith('/auth');

  useEffect(() => {
    if (isExempt) return;
    if (authLoading) return;
    if (!user || !session) {
      checkedToken.current = null; // reset quand l'utilisateur se déconnecte
      return;
    }

    const sessionToken = session.access_token.slice(-20);
    if (checkedToken.current === sessionToken) return; // déjà vérifié pour cette session

    const checkMFA = async () => {
      try {
        // 1. Récupérer la politique MFA depuis la DB (toujours fraîche)
        const { data: settings } = await supabase
          .from('site_settings')
          .select('key, value')
          .in('key', [
            'mfa_required_all',
            'mfa_required_admins',
            'mfa_required_employees',
            'mfa_required_vendors',
            'mfa_required_relays',
            'mfa_required_clients',
          ]);

        const map: Record<string, boolean> = {};
        settings?.forEach((row) => {
          const raw = row.value;
          map[row.key] = raw === true || raw === 'true';
        });

        // 2. Calculer si ce profil a besoin du MFA
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const { data: profileData } = await supabase
          .from('profiles')
          .select('system_role')
          .eq('id', authUser?.id ?? '')
          .single();

        const role = (profileData as any)?.system_role ?? 'client';
        const mfaNeeded =
          map['mfa_required_all'] ||
          ((role === 'admin' || role === 'super_admin') && map['mfa_required_admins']) ||
          (role === 'hr'     && map['mfa_required_employees']) ||
          (role === 'vendor' && map['mfa_required_vendors']) ||
          (role === 'relay'  && map['mfa_required_relays']) ||
          (role === 'client' && map['mfa_required_clients']);

        if (!mfaNeeded) {
          checkedToken.current = sessionToken;
          return;
        }

        // 3. Vérifier si l'appareil est de confiance (mémorisé)
        const trusted = await checkTrusted(user.id);
        if (trusted) {
          checkedToken.current = sessionToken;
          return;
        }

        // 4. Vérifier le niveau d'assurance actuel
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (!aal) { checkedToken.current = sessionToken; return; }

        if (aal.currentLevel === 'aal2') {
          // MFA déjà validé pour cette session
          checkedToken.current = sessionToken;
          return;
        }

        if (aal.nextLevel === 'aal2') {
          // Facteur inscrit mais pas encore validé cette session
          navigate('/mfa?mode=verify', { state: { from: location.pathname }, replace: true });
        } else {
          // Aucun facteur inscrit → enrollment obligatoire
          navigate('/mfa?mode=enroll', { state: { from: location.pathname }, replace: true });
        }
      } catch {
        // En cas d'erreur on laisse passer (fail-open)
        checkedToken.current = session.access_token.slice(-20);
      }
    };

    checkMFA();
  }, [authLoading, user?.id, session?.access_token]);

  return <>{children}</>;
}
